import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Drawer, Radio } from "@outpave/ds";
import { Center } from "components/layout/reusableLayouts";
import { Button, FormControlLabel, FormLabel, RadioGroup } from "@mui/material";
import { FormikProps } from "formik";

// Local components
import ExistingContactPopover from "./ExistingContactPopover";
import type { ExistingContact } from "./ExistingContactPopover";

// Local utilities and validations
import {
  ContactLoadingStates,
  FormikContact,
} from "../../../hooks/useContactData";
import { FormikCustomField } from "hooks/customFields.types";
import useCheckIfContactAlreadyExists from "../../../hooks/useCheckIfContactAlreadyExists";

// Enums and constants
import ContactType from "../../../enums/ContactType";
import { ContactRouterDrawerType } from "../../../enums/ContactRouterDrawerType";
import EditBusinessContact from "./EditBusinessContact";
import EditIndividualContact from "./EditIndividualContact";
import CustomSkeleton from "../../common/CustomSkeleton";
import ContactDrawerRouter from "./ContactDrawerRouter";
import ConfigureAddressDrawer from "components/common/ConfigureAddressDrawer";
import { useFormikHelpers } from "hooks/useFormikHelpers";

interface EditContactDrawerProps {
  open: boolean;
  drawerType: ContactRouterDrawerType;
  formik: FormikProps<FormikContact>;
  customFieldSettings?: FormikCustomField[];
  addingBusinesstoIndividual?: boolean;
  startIndividualWithBusinessId?: boolean;
  specificLoadingStates: ContactLoadingStates;
  onSave: () => void;
  onCancel: (bypassDirtyCheck?: boolean) => void;
  reactivateContact: (contactId: string) => Promise<any>;
  handleOpenAddSaveNewBusinessDrawer?: () => void;
  /** Fires once the drawer has slid out. */
  onAfterClose?: () => void;
}

const EditContactDrawer: React.FC<EditContactDrawerProps> = ({
  open,
  drawerType,
  formik,
  customFieldSettings,
  addingBusinesstoIndividual = false,
  startIndividualWithBusinessId = false,
  specificLoadingStates,
  onSave,
  onCancel,
  reactivateContact,
  handleOpenAddSaveNewBusinessDrawer = () => {},
  onAfterClose,
}) => {
  const { t } = useTranslation();

  const { validateAndTouch } = useFormikHelpers(formik);

  const [emailBlurred, setEmailBlurred] = useState(false);
  const popoverReactivateAnchorRef = useRef<HTMLDivElement>(null);
  const popoverExistingContactRef = useRef<HTMLDivElement>(null);
  const [shouldOpenExistingConstactPopover, setOpenExistingContactPopover] =
    useState(false);
  const [openAddressDrawer, setOpenAddressDrawer] = useState(false);

  const [businessContactIdForNavigation, setBusinessContactIdForNavigation] =
    useState<string | null>(null);

  const contactFormik = formik;
  const contactId = contactFormik.values.id || "";

  const handleCloseExistingContactPopover = async (
    currentContact: ExistingContact | null,
    shouldReactivate?: boolean,
  ) => {
    if (shouldReactivate && currentContact) {
      await reactivateContact(currentContact.id);
      setOpenExistingContactPopover(false);
      return;
    }

    // (means they clicked copy on the popover)
    if (currentContact) {
      if (contactFormik.values.contactType === ContactType.Individual) {
        contactFormik.setFieldValue("firstName", currentContact.firstName);
        contactFormik.setFieldValue("lastName", currentContact.lastName);
        contactFormik.setFieldValue("title", currentContact.title);
      }
      if (contactFormik.values.contactType === ContactType.Business) {
        contactFormik.setFieldValue("website", currentContact.website);
      }
      contactFormik.setFieldValue("businessName", currentContact.businessName);
      contactFormik.setFieldValue(
        "parentContactId",
        currentContact.parentContactId,
      );
      contactFormik.setFieldValue("email", currentContact.email);
      contactFormik.setFieldValue("phone", currentContact.phone);
      contactFormik.setFieldValue("address", currentContact.address);
      contactFormik.setFieldValue(
        "relationshipType",
        currentContact.relationshipType,
      );
    }

    setOpenExistingContactPopover(false);
  };

  const shouldCheckContactExists =
    (contactFormik.values.contactType === ContactType.Individual &&
      emailBlurred) ||
    (contactFormik.values.contactType === ContactType.Business &&
      contactFormik.values.website);

  const nameToMatch =
    contactFormik.values.contactType === ContactType.Business
      ? contactFormik.values.website
      : contactFormik.values.email;

  const fieldToMatch =
    contactFormik.values.contactType === ContactType.Business
      ? "website"
      : "email";

  const {
    existingContact,
    error: errorContactFieldAlreadyExists,
    loadingCheckIfContactAlreadyExists,
  } = useCheckIfContactAlreadyExists(
    shouldCheckContactExists ? nameToMatch : null,
    fieldToMatch,
    () => setEmailBlurred(false),
    contactId ? contactId : null,
  );

  const currenctContactType = contactFormik.values.contactType;
  const getDrawerTitle = (
    drawerType: ContactRouterDrawerType,
    contactType: ContactType | null,
    contactId?: string,
  ) => {
    if (contactId) {
      if (contactType === ContactType.Business)
        return [t("Edit Business Contact")];
      if (contactType === ContactType.Individual)
        return [t("Edit Individual Contact")];
      return [t("Edit Contact")];
    }
    if (drawerType === ContactRouterDrawerType.NewContact) {
      if (contactType === ContactType.Business)
        return [t("Add Business Contact")];
      if (contactType === ContactType.Individual)
        return [t("Add Individual Contact")];
    }
    return [t("Add Contact")];
  };

  const contactDrawerTitle = contactFormik.values.address?.address1
    ? [t("Edit Address")]
    : [t("Add Address")];

  const busy =
    specificLoadingStates.updatingContact ||
    specificLoadingStates.loadingContact ||
    specificLoadingStates.reactivatingContact ||
    specificLoadingStates.invitingContact;

  return (
    <>
      <Drawer
        open={open}
        onClose={() => onCancel()}
        onAfterClose={onAfterClose}
        title={getDrawerTitle(
          drawerType,
          contactFormik.values.contactType,
          contactFormik.values.id,
        )}
        loading={busy}
        footer={
          <>
            <Button variant="primary-outlined" onClick={() => onCancel()}>
              {drawerType === ContactRouterDrawerType.NewContact
                ? t("Exit")
                : t("Back")}
            </Button>

            <Button
              variant="primary-filled"
              onClick={async () => {
                const isValid = await validateAndTouch();
                if (isValid) {
                  onSave();
                }
              }}
              disabled={
                !contactFormik.values.contactType ||
                !!errorContactFieldAlreadyExists ||
                !contactFormik.dirty ||
                specificLoadingStates.updatingContact
              }
            >
              {drawerType === ContactRouterDrawerType.NewContact
                ? t("Next")
                : t("Save")}
            </Button>
          </>
        }
      >
        {specificLoadingStates.loadingContact && !contactFormik.values.id && (
          <CustomSkeleton
            rows={6}
            variant="form"
            formFieldWidths={["100%", "80%", "100%", "60%", "100%", "100%"]}
            textHeight={40}
            gap={4}
          />
        )}
        {drawerType === ContactRouterDrawerType.NewContact &&
          !addingBusinesstoIndividual &&
          !startIndividualWithBusinessId && (
            <>
              <FormLabel component="legend">
                {t("Choose what type of contact this is")}
              </FormLabel>
              <Center>
                <RadioGroup
                  aria-label="contactType"
                  name="contactType"
                  value={contactFormik.values.contactType}
                  onChange={(e) =>
                    contactFormik.setFieldValue("contactType", e.target.value)
                  }
                  row
                  sx={{
                    gap: "var(--space-7)",
                    paddingBlock: "var(--space-3)",
                  }}
                >
                  <FormControlLabel
                    value={ContactType.Business}
                    control={<Radio size="small" />}
                    label={t("Business")}
                  />
                  <FormControlLabel
                    value={ContactType.Individual}
                    control={<Radio size="small" />}
                    label={t("Individual")}
                  />
                </RadioGroup>
              </Center>
            </>
          )}

        {currenctContactType === ContactType.Business && (
          <EditBusinessContact
            contactFormik={contactFormik}
            customFieldSettings={customFieldSettings}
            errorContactFieldAlreadyExists={errorContactFieldAlreadyExists}
            setOpenAddressDrawer={setOpenAddressDrawer}
            setOpenExistingContactPopover={setOpenExistingContactPopover}
            popoverExistingContactRef={popoverExistingContactRef}
          />
        )}

        {currenctContactType === ContactType.Individual && (
          <EditIndividualContact
            contactFormik={contactFormik}
            customFieldSettings={customFieldSettings}
            errorContactFieldAlreadyExists={errorContactFieldAlreadyExists}
            loadingCheckIfContactAlreadyExists={
              loadingCheckIfContactAlreadyExists
            }
            popoverExistingContactRef={popoverExistingContactRef}
            popoverReactivateAnchorRef={popoverReactivateAnchorRef}
            setOpenAddressDrawer={setOpenAddressDrawer}
            setOpenExistingContactPopover={setOpenExistingContactPopover}
            handleOpenAddSaveNewBusinessDrawer={
              handleOpenAddSaveNewBusinessDrawer
            }
            setEmailBlurred={setEmailBlurred}
            reactivateContact={reactivateContact}
            handleLinkToBusinessAccount={(businessContactId) => {
              setBusinessContactIdForNavigation(businessContactId);
            }}
          />
        )}
      </Drawer>

      <ConfigureAddressDrawer
        open={openAddressDrawer}
        drawerTitle={contactDrawerTitle}
        initialValues={contactFormik.values.address}
        onClose={() => setOpenAddressDrawer(false)}
        onSuccess={async (values) => {
          contactFormik.setFieldValue("address", {
            ...contactFormik.values.address,
            ...values,
          });
          setOpenAddressDrawer(false);
        }}
      />

      {businessContactIdForNavigation && (
        <ContactDrawerRouter
          open={!!businessContactIdForNavigation}
          drawerType={ContactRouterDrawerType.ExistingContact}
          contactId={businessContactIdForNavigation}
          onClose={() => setBusinessContactIdForNavigation(null)}
        />
      )}

      {shouldOpenExistingConstactPopover && existingContact && (
        <ExistingContactPopover
          anchorEl={popoverExistingContactRef.current}
          onClose={handleCloseExistingContactPopover}
          open={shouldOpenExistingConstactPopover}
          existingContact={existingContact}
        />
      )}
    </>
  );
};

export default EditContactDrawer;
